import { findCableByCurrent, findAOSForCable, findKAKRByCurrent } from '../../../electrical/electricalFunctions';
import { CABLES, MCB, RCD } from '../../../electrical/Malzeme';
import { COMPONENT_KINDS, FEEDER_KINDS } from '../../../types/SimpleSupplyPoint';

/**
 * Panel elektriksel güncellemeler için servis
 * Senaryo: demand power → current → feeder → breaker
 * Kural: feederCurrentCap > current > breakerAmperaj
 */
export class ElectricalUpdateService {

  /**
   * Panel demand power'ına göre current hesaplar
   * @param {number} demandPower - Talep gücü (W)
   * @param {number} phaseCount - Faz sayısı (1 veya 3)
   * @returns {number} Akım (A)
   */
  static calculateCurrentFromPower(demandPower, phaseCount = 3) {
    if (!demandPower || demandPower === 0) return 0;

    const voltage = phaseCount === 3 ? 380 : 220;
    const powerFactor = 0.9; // Cos φ

    if (phaseCount === 3) {
      return demandPower / (voltage * Math.sqrt(3) * powerFactor);
    } else {
      return demandPower / (voltage * powerFactor);
    }
  }

  /**
   * Current'a göre uygun feeder cable'ı bulur
   * @param {number} current - Akım (A)
   * @param {number} sectionCount - İletken sayısı (mevcut feeder'dan alınır)
   * @param {string} cableType - Kablo tipi (NYY, NYM vb.)
   * @returns {object|null} Cable objesi veya null
   */
  static findFeederCable(current, sectionCount = 4, cableType = 'NYY') {
    const tolerans = 0; // ✅ Tolerans 0 çünkü currentDemand zaten güvenlik faktörlü
    const minKesit = 6; // Minimum kesit
    const kapType = 'airCurCap'; // Havada akım taşıma kapasitesi

    return findCableByCurrent(current, sectionCount, kapType, minKesit, tolerans, cableType);
  }

  /**
   * Current ve feeder capacity'ye göre uygun breaker bulur
   * @param {number} current - Panel akımı (A)
   * @param {number} feederCurrentCap - Feeder akım taşıma kapasitesi (A)
   * @param {number} phaseCount - Faz sayısı
   * @param {string} currentCurve - Mevcut breaker eğrisi (korunur)
   * @returns {object|null} MCB objesi veya null
   */
  static findMainBreaker(current, feederCurrentCap, phaseCount = 3, currentCurve = 'C') {
    const minCurrent = Math.ceil(current); // Panel akımından büyük olmalı
    const maxCurrent = feederCurrentCap; // Feeder kapasitesinden küçük olmalı
    const tolerans = 10; // %10 tolerans
    const curve = currentCurve; // ✅ Mevcut eğriyi koru
    const shortCircuitCap = 10; // 10kA kısa devre kapasitesi

    return findAOSForCable(maxCurrent, minCurrent, phaseCount, minCurrent, curve, shortCircuitCap, tolerans);
  }

  /**
   * Panel'e load eklendiğinde gerekli elektriksel güncellemeleri yapar
   * @param {object} panelComponent - Panel component
   * @param {object} feederComponent - Panel'e gelen feeder
   * @param {object} simpleSupply - SimpleSupply state
   * @returns {object} Güncelleme sonuçları {needsFeederUpdate, needsBreakerUpdate, newCable, newBreaker}
   */
  static calculateElectricalUpdates(panelComponent, feederComponent, simpleSupply) {
    const results = {
      needsFeederUpdate: false,
      needsBreakerUpdate: false,
      newCable: null,
      newBreaker: null,
      newRCD: null,
      currentDemand: 0,
      currentFeederCap: 0
    };

    if (!panelComponent?.electrical?.demandPower) {
      return results;
    }
    // 1. Panel demand power'dan current hesapla
    const demandPower = panelComponent.electrical.demandPower;
    const phaseCount = panelComponent.electrical.phaseCount || 3;
    const currentDemand = this.calculateCurrentFromPower(demandPower, phaseCount);

    results.currentDemand = currentDemand;

    // 2. Mevcut feeder cable kapasitesini bul
    let currentFeederCap = 0;
    if (feederComponent?.cable?.indexNo) {
      const currentCable = CABLES[feederComponent.cable.indexNo];
      currentFeederCap = currentCable?.airCurCap || 0;
    }

    results.currentFeederCap = currentFeederCap;

    // 3. Feeder kapasitesi yeterli mi kontrol et
    if (currentDemand > currentFeederCap * 0.8) { // %80 güvenlik faktörü
      results.needsFeederUpdate = true;

      // Mevcut feeder'ın sectionCount bilgisini al (varsayılan 4)
      const currentSectionCount = feederComponent?.cable?.sectionCount || 4;

      // Yeni cable bul - mevcut sectionCount korunur
  const currentCableType = feederComponent?.cable?.type || 'NYY';
  results.newCable = this.findFeederCable(currentDemand, currentSectionCount, currentCableType);
      if (results.newCable) {
        results.currentFeederCap = results.newCable.airCurCap;
      }
    }

    // 4. Breaker kontrol et
    const newFeederCap = results.newCable?.airCurCap || currentFeederCap;
    if (newFeederCap > 0) {
      // Mevcut breaker bilgilerini al
      const currentBreakerEntry = panelComponent.entries?.find(e => e.kind === 'main_breaker');
      const currentBreaker = currentBreakerEntry ? MCB[currentBreakerEntry.indexNo] : null;
      const currentBreakerCurrent = currentBreaker?.current || 0;
      const currentBreakerCurve = currentBreaker?.curve || 'C'; // ✅ Mevcut eğriyi al

      results.newBreaker = this.findMainBreaker(currentDemand, newFeederCap, phaseCount, currentBreakerCurve);

      // Mevcut breaker ile karşılaştır

      if (results.newBreaker && results.newBreaker.current !== currentBreakerCurrent) {
        results.needsBreakerUpdate = true;
      }

      // RCD de bul - breaker amperajıyla aynı amperajda
      if (results.newBreaker) {
        // Mevcut RCD'nin özelliklerini koru, sadece amperajını güncelle
        const currentRcdEntry = panelComponent.entries?.find(e =>
          e.kind === 'protective_device' || e.kind === 'main_protective_device'
        );

        let sensitivity = 30; // Default 30mA
        let pole = phaseCount; // Faz sayısına göre
       if (currentRcdEntry && RCD[currentRcdEntry.indexNo]) {
          const currentRcd = RCD[currentRcdEntry.indexNo];
          sensitivity = currentRcd.sensitivity; // Mevcut hassasiyeti koru
          pole = currentRcd.polar; // Mevcut pole sayısını koru

         }

        results.newRCD = findKAKRByCurrent(results.newBreaker.current, pole, sensitivity, 'A');

        // Eğer tam eşleşen RCD bulunamadıysa, en yakın ve uygun RCD'yi bul
        if (!results.newRCD) {
          // Aynı pole ve sensitivity ile >= current olan en küçük RCD'yi bul
          const fallbackRCD = RCD.find(rcd =>
            rcd.polar === pole &&
            rcd.sensitivity === sensitivity &&
            rcd.current >= results.newBreaker.current
          );

          // Eğer hala bulamadıysa, sadece pole ile eşleşen en büyük RCD'yi al
          if (!fallbackRCD) {
            const maxCurrentRCD = RCD
              .filter(rcd => rcd.polar === pole)
              .sort((a, b) => b.current - a.current)[0]; // En büyük current'ı al

            if (maxCurrentRCD) {
              results.newRCD = maxCurrentRCD;
            }
          } else {
            results.newRCD = fallbackRCD;
          }
        }

      }
    }

    return results;
  }

  /**
   * Component ve feeder'ları elektriksel hesaplama sonuçlarına göre günceller
   * @param {object} panelComponent - Güncellenecek panel
   * @param {object} feederComponent - Güncellenecek feeder
   * @param {object} updateResults - calculateElectricalUpdates sonuçları
   * @returns {object} Güncelleme action'ları {componentUpdates, feederUpdates}
   */
  static generateUpdateActions(panelComponent, feederComponent, updateResults) {
    const actions = {
      componentUpdates: [],
      feederUpdates: []
    };
    // Panel component güncellemesi
    if (updateResults.currentDemand > 0) {
      const panelUpdates = {
        id: panelComponent.id,
        changes: {
          electrical: {
            ...panelComponent.electrical,
            current: updateResults.currentDemand
          }
        }
      };

      // Breaker güncelleme
      if (updateResults.needsBreakerUpdate && updateResults.newBreaker) {
        const entries = [...(panelComponent.entries || [])];
        const mainBreakerIndex = entries.findIndex(e => e.kind === 'main_breaker');

        if (mainBreakerIndex >= 0) {
          entries[mainBreakerIndex] = {
            ...entries[mainBreakerIndex],
            indexNo: this.findMCBIndex(updateResults.newBreaker)
          };
        }

        // RCD güncelleme
        if (updateResults.newRCD) {
          const rcdIndex = entries.findIndex(e => e.kind === 'protective_device'||e.kind === 'main_protective_device');
          if (rcdIndex >= 0) {
            entries[rcdIndex] = {
              ...entries[rcdIndex],
              indexNo: this.findRCDIndex(updateResults.newRCD)
            };
          }
        }

        panelUpdates.changes.entries = entries;
      }

      actions.componentUpdates.push(panelUpdates);
    }

    // Feeder güncellemesi
    if (updateResults.needsFeederUpdate && updateResults.newCable && feederComponent) {
      actions.feederUpdates.push({
        id: feederComponent.id,
        changes: {
          cable: {
            ...feederComponent.cable,
            indexNo: this.findCableIndex(updateResults.newCable),
            type: updateResults.newCable.type,
            section: updateResults.newCable.section,
            currentCap: updateResults.newCable.airCurCap,
            // ✅ Preserve original sectionCount - don't override with newCable.sectionCount
            sectionCount: feederComponent.cable.sectionCount
          },
          electrical: {
            ...feederComponent.electrical,
            current: updateResults.currentDemand,
            currentCapacity: updateResults.newCable.airCurCap
          }
        }
      });
    }

    return actions;
  }

  // Helper functions
  static findCableIndex(cable) {
    return CABLES.findIndex(c =>
      c.type === cable.type &&
      c.section === cable.section &&
      c.sectionCount === cable.sectionCount
    );
  }

  static findMCBIndex(mcb) {
    return MCB.findIndex(m =>
      m.current === mcb.current &&
      m.polar === mcb.polar &&
      m.curve === mcb.curve
    );
  }

  static findRCDIndex(rcd) {
    return RCD.findIndex(r =>
      r.current === rcd.current &&
      r.polar === rcd.polar &&
      r.sensitivity === rcd.sensitivity
    );
  }
}